import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CheckIcon from '@mui/icons-material/Check'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Chip, ClickAwayListener, InputBase, Paper, Popper, Typography } from '@mui/material'

import { RootState } from '~redux/rootReducer'
import { useListStaffersQuery } from '~redux/slices/apiSlice'
import { Identity0 } from '~types'

import Avatar from './Avatar'

interface IdentitySelectorProps {
  selectedIdentities: Identity0[]
  onChange: (selectedIdentities: Identity0[]) => void
  multiSelect: boolean
  identityIds?: string[]
  placeholder?: string
  style?: React.CSSProperties
}

const IdentitySelector: React.FC<IdentitySelectorProps> = ({
  selectedIdentities,
  onChange,
  multiSelect = true,
  identityIds = undefined,
  placeholder = 'Select identities...',
  style,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)

  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const anchorRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { data: queryResponse } = useListStaffersQuery(
    {
      user_id: userId as string,
      identity_ids: identityIds,
      limit: 7,
      query: searchQuery,
    },
    { skip: !userId },
  )
  const results = queryResponse?.identities ?? []

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value)
  }

  const handleIdentityToggle = (identity: Identity0): void => {
    let newSelectedIdentities: Identity0[]

    if (multiSelect) {
      newSelectedIdentities = selectedIdentities.some((i) => i.id === identity.id)
        ? selectedIdentities.filter((i) => i.id !== identity.id)
        : [...selectedIdentities, identity]
    } else {
      if (selectedIdentities.some((i) => i.id === identity.id)) {
        newSelectedIdentities = []
      } else {
        newSelectedIdentities = [identity]
      }
      setIsOpen(false)
    }

    onChange(newSelectedIdentities)
  }

  const handleClose = (): void => {
    setIsOpen(false)
    setSearchQuery('')
    setFocusedIndex(-1)
  }

  const handleToggle = (): void => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setFocusedIndex(-1)
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 0)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    }
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(0)
        break
      case 'Escape':
        handleClose()
        break
    }
  }

  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    identity: Identity0,
    index: number,
  ): void => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        handleIdentityToggle(identity)
        break
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(index < results.length - 1 ? index + 1 : 0)
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(index > 0 ? index - 1 : results.length - 1)
        break
      case 'Tab':
        if (!e.shiftKey && index === results.length - 1) {
          e.preventDefault()
          setFocusedIndex(0)
        } else if (e.shiftKey && index === 0) {
          e.preventDefault()
          setFocusedIndex(results.length - 1)
        }
        break
      case 'Escape':
        handleClose()
        break
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1)
    }
  }, [isOpen])

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        sx={{ position: 'relative', width: 'auto', minWidth: '250px', maxWidth: '400px', ...style }}
      >
        <Box
          ref={anchorRef}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: '#000' },
            '&:focus-visible': {
              border: '2px solid #ffffff',
              outline: '2px solid #0894cf',
              boxShadow: '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
            },
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, overflow: 'hidden' }}>
            {selectedIdentities.length === 0 ? (
              <Typography color='text.secondary'>{placeholder}</Typography>
            ) : (
              <>
                {selectedIdentities.map((identity) => (
                  <Chip
                    key={identity.id}
                    avatar={
                      <Avatar
                        src={identity.image || ''}
                        alt={identity.name === '' ? identity.email || '' : identity.name}
                        size='24px'
                        backgroundColor='#F0F0F0'
                      />
                    }
                    label={identity.name === '' ? identity.email || '' : identity.name}
                    onDelete={() => handleIdentityToggle(identity)}
                    sx={{
                      ml: 0,
                      mr: 0.5,
                      maxWidth: '100%',
                      '& .MuiChip-avatar': {
                        ml: '4px',
                        mr: '-4px',
                      },
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                ))}
              </>
            )}
          </Box>
          <ExpandMoreIcon
            sx={{
              transition: 'transform 0.3s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ width: anchorRef.current?.clientWidth, maxWidth: '400px', zIndex: 1300 }}
        >
          <Paper sx={{ mt: 1, p: 2 }}>
            <InputBase
              inputRef={searchInputRef}
              fullWidth
              placeholder='Search identities...'
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              startAdornment={<SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />}
              sx={{
                '&:focus-within': {
                  borderRadius: '4px',
                },
              }}
            />
            <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              {results.map((identity, index) => (
                <Box
                  key={identity.id}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => handleIdentityToggle(identity)}
                  onKeyDown={(e) => handleOptionKeyDown(e, identity, index)}
                  ref={
                    index === focusedIndex
                      ? (el: HTMLDivElement | null): void => el?.focus()
                      : undefined
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:focus': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }} gap={1}>
                    <Avatar
                      src={identity.image || ''}
                      alt={identity.name === '' ? identity.email || '' : identity.name}
                      size='24px'
                      backgroundColor='#F0F0F0'
                    />
                    <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {identity.name === '' ? identity.email || '' : identity.name}
                    </Typography>
                  </Box>
                  {selectedIdentities.some((i) => i.id === identity.id) && (
                    <CheckIcon sx={{ color: 'success.main', flexShrink: 0 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default IdentitySelector
